/**
 * The unrolled borehole wall, and everything drawn on top of it.
 *
 * Two layers on purpose. The image itself goes onto a `<canvas>`, because it is
 * up to four thousand rows of pixels and an SVG of that many rectangles is a
 * document the browser has to lay out. The interpretation — sinusoids, picks,
 * breakout sectors — goes onto an `<svg>` above it, because those are a few
 * dozen shapes that need to be hit-tested, hovered and coloured by CSS.
 */

import { useRampsVersion } from "../hooks";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  type Breakout,
  type Feature,
  type ImageWindow,
  bearing,
  paint,
  type ByteStretch,
  pixelsFrom,
  sinusoidPath,
} from "../lib/imagelog";

export interface PickPoint {
  azimuth: number;
  md: number;
}

export function ImageCanvas({
  win,
  which,
  ramp,
  reverse,
  stretch,
  features,
  holeDiameter,
  breakouts,
  picks,
  ghost,
  selectedId,
  height,
  width,
  showSinusoids = true,
  onPick,
  onSelect,
  label,
}: {
  win: ImageWindow | null;
  which: "static" | "dynamic";
  ramp: string;
  reverse?: boolean;
  /** Rentang bita yang direntang ke seluruh ramp; lihat `paint`. */
  stretch?: ByteStretch;
  features: Feature[];
  holeDiameter: number;
  breakouts?: Breakout[];
  picks?: PickPoint[];
  ghost?: { md: number; apparent_dip: number; apparent_azimuth: number } | null;
  selectedId?: number | null;
  height: number;
  width: number;
  showSinusoids?: boolean;
  onPick?: (point: PickPoint) => void;
  onSelect?: (feature: Feature) => void;
  label?: ReactNode;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pixels = useMemo(() => (win ? pixelsFrom(win, which) : null), [win, which]);
  const versi = useRampsVersion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pixels) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    canvas.width = pixels.bins;
    canvas.height = pixels.rows;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.putImageData(paint(pixels, ramp, reverse, stretch), 0, 0);
  }, [pixels, ramp, reverse, stretch?.min, stretch?.max, versi]);

  const top = win?.top ?? 0;
  const base = win?.base ?? 1;
  const span = base - top || 1;
  const yFor = (md: number) => ((md - top) / span) * height;
  const xFor = (azimuth: number) => (azimuth / 360) * width;

  const pick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!onPick || !win) return;
    const box = event.currentTarget.getBoundingClientRect();
    const azimuth = ((event.clientX - box.left) / box.width) * 360;
    const md = top + ((event.clientY - box.top) / box.height) * span;
    onPick({ azimuth: Math.max(0, Math.min(360, azimuth)), md });
  };

  return (
    <div style={{ position: "relative", width, height, flex: "none" }}>
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          // Tanpa penghalusan. Sebuah baris citra adalah satu cuplikan
          // kedalaman, dan interpolasi bilinear mengarang setengah baris di
          // antara dua bacaan — persis pada skala tempat rekahan rambut
          // dibaca.
          imageRendering: "pixelated",
          background: "var(--panel-inset)",
        }}
      />
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        onClick={pick}
        style={{
          position: "absolute",
          inset: 0,
          cursor: onPick ? "crosshair" : "default",
        }}
      >
        {/* Sektor breakout, sebagai pita tegak di dua sisi berseberangan. */}
        {breakouts?.map((row) =>
          [row.azimuth, (row.azimuth + 180) % 360].map((azimuth, side) => {
            const half = (row.width / 2 / 360) * width;
            return (
              <rect
                key={`${row.id}-${side}`}
                x={xFor(azimuth) - half}
                y={yFor(row.top_md)}
                width={half * 2}
                height={Math.max(yFor(row.base_md) - yFor(row.top_md), 1)}
                fill={row.kind === "breakout" ? "#c2185b" : "#f2c200"}
                opacity={0.24}
                stroke={row.kind === "breakout" ? "#c2185b" : "#f2c200"}
                strokeWidth={0.7}
              >
                <title>
                  {row.kind_label} · {row.top_md.toFixed(1)}–{row.base_md.toFixed(1)} m ·
                  {" "}azimuth {bearing(row.azimuth)}°
                </title>
              </rect>
            );
          }),
        )}

        {showSinusoids &&
          features.map((feature) => {
            const points = sinusoidPath(feature, holeDiameter, 6)
              .map(([azimuth, md]) => `${xFor(azimuth).toFixed(1)},${yFor(md).toFixed(1)}`)
              .join(" ");
            const chosen = feature.id === selectedId;
            return (
              <polyline
                key={feature.id}
                points={points}
                fill="none"
                stroke={feature.color || "#35b8c6"}
                strokeWidth={chosen ? 2.4 : 1.3}
                strokeDasharray={feature.kind.includes("frac") ? undefined : "5 3"}
                opacity={chosen ? 1 : 0.85}
                style={{ cursor: onSelect ? "pointer" : undefined }}
                onClick={(event) => {
                  if (!onSelect) return;
                  // Kalau tidak dihentikan, klik yang sama juga jatuh ke svg
                  // dan menambahkan sebuah titik petik — memilih sebuah bidang
                  // akan selalu ikut memulai petikan baru di atasnya.
                  event.stopPropagation();
                  onSelect(feature);
                }}
              >
                <title>
                  {feature.kind_label} · {feature.md.toFixed(2)} m · dip{" "}
                  {feature.dip.toFixed(1)}° / {bearing(feature.azimuth)}°
                </title>
              </polyline>
            );
          })}

        {ghost && (
          <polyline
            points={sinusoidPath(ghost, holeDiameter, 6)
              .map(([azimuth, md]) => `${xFor(azimuth).toFixed(1)},${yFor(md).toFixed(1)}`)
              .join(" ")}
            fill="none"
            stroke="var(--accent)"
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        )}

        {picks?.map((point, i) => (
          <circle
            key={i}
            cx={xFor(point.azimuth)}
            cy={yFor(point.md)}
            r={3.4}
            fill="var(--accent)"
            stroke="#fff"
            strokeWidth={1.2}
          />
        ))}
      </svg>
      {label && (
        <div
          style={{
            position: "absolute",
            top: 4,
            left: 6,
            font: "500 10px var(--font-mono)",
            color: "var(--ink)",
            background: "color-mix(in srgb, var(--panel) 78%, transparent)",
            padding: "1px 5px",
            borderRadius: 3,
            pointerEvents: "none",
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

/**
 * The azimuth ruler above an unrolled image.
 *
 * Labelled by compass point rather than by degrees alone: the thing a reader
 * checks on a breakout is which *side* of the hole failed, and "W" answers that
 * in one glance where "270" needs a beat of arithmetic.
 */
export function AzimuthRuler({ width }: { width: number }) {
  const marks = [
    [0, "N"], [90, "E"], [180, "S"], [270, "W"], [360, "N"],
  ] as const;
  return (
    <svg viewBox={`0 0 ${width} 14`} width={width} height={14} style={{ flex: "none" }}>
      {marks.map(([azimuth, name]) => {
        const x = (azimuth / 360) * width;
        return (
          <g key={azimuth}>
            <line x1={x} y1={9} x2={x} y2={14} stroke="var(--line-strong)" strokeWidth={1} />
            <text
              x={Math.min(Math.max(x, 6), width - 6)}
              y={7}
              textAnchor="middle"
              style={{ font: "500 8.5px var(--font-mono)", fill: "var(--ink-5)" }}
            >
              {name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
